import { create } from 'zustand'

export type ToastKind = 'success' | 'error' | 'info'

export interface Toast {
  id: string
  kind: ToastKind
  title?: string
  message: string
  createdAt: number
  durationMs: number | null
}

interface ToastState {
  toasts: Toast[]
  push: (t: Omit<Toast, 'id' | 'createdAt' | 'durationMs'> & { durationMs?: number | null }) => string
  dismiss: (id: string) => void
  clear: () => void
}

const DEFAULTS: Record<ToastKind, number | null> = {
  success: 4000,
  info: 4000,
  error: null,
}

function makeId(): string {
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    const id = makeId()
    const durationMs = t.durationMs === undefined ? DEFAULTS[t.kind] : t.durationMs
    set((s) => ({
      toasts: [...s.toasts, { ...t, id, createdAt: Date.now(), durationMs }],
    }))
    return id
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
  clear: () => set({ toasts: [] }),
}))

// Keys that already fired this session. Background loops (snapshot/sample
// persistence) call toastOnce every tick on failure — one notification is
// signal, one per tick is noise.
const onceKeys = new Set<string>()

export const toastOnce = {
  error: (key: string, message: string, title?: string): string | null => {
    if (onceKeys.has(key)) return null
    onceKeys.add(key)
    return useToastStore.getState().push({ kind: 'error', message, title })
  },
}

export const toast = {
  success: (message: string, title?: string) =>
    useToastStore.getState().push({ kind: 'success', message, title }),
  error: (message: string, title?: string) =>
    useToastStore.getState().push({ kind: 'error', message, title }),
  info: (message: string, title?: string) =>
    useToastStore.getState().push({ kind: 'info', message, title }),
}
